import { htmlToPlainText } from 'libs/text'
import { isNil } from 'ramda'
import { Typography, withStyles } from '@material-ui/core'

import { NORMAL, DISTRESS } from 'app/constants/statusColors'

import StatusIcon from 'app/componentV4/Status/Icon'
import LightTooltip from 'app/componentV4/Tooltip/LightTooltip'

import {
  GENERATE_CONTENT,
  TEXT_REVISE,
  GENERATE_IMAGE,
  TEXT_SUMMARIZE,
  TEXT_RATING,
  keyGenerateContent,
  keyGenerateImage,
  contentLengthOptions,
  messageToneOptions,
  improveWritingOptions,
  imageStyleOptions,
  imageRatioOptions,
} from 'app/constants/ai'

import {
  AI_LOG_DISCARDED,
  AI_LOG_CREATED,
} from 'app/main/plansAndBilling/constants'

const { contentLength, messageTone, improveWriting } = keyGenerateContent
const { imageStyle, imageRatio } = keyGenerateImage

const CustomLightTooltip = withStyles({
  tooltip: {
    width: '350px',
  },
})(LightTooltip)

export const handleStatus = (status) => {
  switch (status) {
    case AI_LOG_DISCARDED:
      return DISTRESS
    case AI_LOG_CREATED:
      return NORMAL
    default:
      return ''
  }
}

export const handleTitle = (title, status, formatMessage) => {
  switch (status) {
    case AI_LOG_DISCARDED:
      return (
        <div className="space-x-3 flex items-center">
          <StatusIcon type={handleStatus(status)} />
          <Typography className="text-16 text-grey-400 font-bold line-clamp-2">
            {formatMessage({
              id: 'aiUsageLog.empty.message.title',
            })}
          </Typography>
        </div>
      )
    case AI_LOG_CREATED:
      return (
        <div className="space-x-3 flex items-center">
          <StatusIcon type={handleStatus(status)} />
          <Typography className="text-16 text-grey-400 font-bold line-clamp-2">
            {htmlToPlainText(title)}
          </Typography>
        </div>
      )
    default:
      return ''
  }
}

export const renderTitle = (title, status, formatMessage) => {
  const isDiscarded = status === AI_LOG_DISCARDED && isNil(title)

  if (isDiscarded) return handleTitle(title, status, formatMessage)
  else {
    return (
      <CustomLightTooltip
        placement="bottom-start"
        title={
          <Typography className="text-14 text-primary p-5">
            {htmlToPlainText(title)}
          </Typography>
        }
      >
        {handleTitle(title, status, formatMessage)}
      </CustomLightTooltip>
    )
  }
}

const generatedContentDetail = (key, value, formatMessage) => {
  switch (key) {
    case contentLength:
      return formatMessage(
        { id: 'text.length.label' },
        { text: formatMessage({ id: `${contentLengthOptions[value]}` }) },
      )
    case messageTone:
      return formatMessage(
        { id: 'tone.label' },
        { text: formatMessage({ id: `${messageToneOptions[value]}` }) },
      )
    case improveWriting:
      return formatMessage(
        { id: 'improved.label' },
        { text: formatMessage({ id: `${improveWritingOptions[value]}` }) },
      )
    default:
      return ''
  }
}

const generatedImageDetail = (key, value, formatMessage) => {
  switch (key) {
    case imageStyle:
      return formatMessage(
        { id: 'style.label' },
        { text: formatMessage({ id: `${imageStyleOptions[value]}` }) },
      )

    case imageRatio:
      return formatMessage(
        { id: 'orientation.label' },
        { text: formatMessage({ id: `${imageRatioOptions[value]}` }) },
      )
    default:
      return ''
  }
}

const commonsConjunctionAnd = (formatMessage, text) => {
  return formatMessage(
    {
      id: 'conjunction.and.label',
    },
    {
      text: text,
    },
  )
}

const commonsConjunctionWith = (formatMessage, text) => {
  return formatMessage(
    {
      id: 'conjunction.with.label',
    },
    {
      text: text,
    },
  )
}

const commonsConjunctionUsing = (formatMessage, text) => {
  return formatMessage(
    {
      id: 'conjunction.using.label',
    },
    {
      text: text,
    },
  )
}

const deleteOjectValueNull = (object) => {
  for (const key in object) {
    if (object[key] === null) {
      delete object[key]
    }
  }
  return object
}

const handleActionGenContent = (actions, formatMessage) => {
  const length = Object.keys(actions).length

  if (length === 1) {
    const key = Object.keys(actions)[0]
    const value = Object.values(actions)[0]

    if (Object.keys(actions)[0] === improveWriting)
      return commonsConjunctionAnd(
        formatMessage,
        generatedContentDetail(key, value, formatMessage),
      )
    else
      return commonsConjunctionWith(
        formatMessage,
        generatedContentDetail(key, value, formatMessage),
      )
  } else {
    const text = []
    const actionsByPriority = deleteOjectValueNull({
      [contentLength]: actions[contentLength] ?? null,
      [messageTone]: actions[messageTone] ?? null,
      [improveWriting]: actions[improveWriting] ?? null,
    })

    Object.keys(actionsByPriority).forEach((key, index) => {
      if (index === length - 1)
        text.push(
          commonsConjunctionAnd(
            formatMessage,
            generatedContentDetail(key, actionsByPriority[key], formatMessage),
          ),
        )
      else
        text.push(
          commonsConjunctionWith(
            formatMessage,
            generatedContentDetail(key, actionsByPriority[key], formatMessage),
          ),
        )
    })
    return text.join(' ')
  }
}

const handleActionGenImage = (actions, formatMessage) => {
  const actionsByPriority = deleteOjectValueNull({
    [imageStyle]: actions[imageStyle] ?? null,
    [imageRatio]: actions[imageRatio] ?? null,
  })
  const length = Object.keys(actions).length
  if (length === 1)
    return commonsConjunctionUsing(
      formatMessage,
      generatedImageDetail(
        Object.keys(actions)[0],
        Object.values(actions)[0],
        formatMessage,
      ),
    )
  else {
    const text = []
    Object.keys(actionsByPriority).forEach((key, index) => {
      if (isNil(actionsByPriority[key])) return

      if (index === length - 1)
        text.push(
          commonsConjunctionAnd(
            formatMessage,
            generatedImageDetail(key, actionsByPriority[key], formatMessage),
          ),
        )
      else
        text.push(
          commonsConjunctionUsing(
            formatMessage,
            generatedImageDetail(key, actionsByPriority[key], formatMessage),
          ),
        )
    })
    return text.join(' ')
  }
}

const handleAiAction = (type, actions, formatMessage) => {
  if ([GENERATE_CONTENT, TEXT_REVISE].includes(type))
    return ` ${handleActionGenContent(actions, formatMessage)}`
  else if (type === GENERATE_IMAGE)
    return ` ${handleActionGenImage(actions, formatMessage)}`
  else null
}

export const renderDetailActions = (type, actions, formatMessage) => {
  if (isNil(actions)) return ''
  else return handleAiAction(type, actions, formatMessage)
}

export const renderType = (type) => {
  switch (type) {
    case GENERATE_CONTENT:
      return 'aiUsageLog.generate.content.label'
    case TEXT_REVISE:
      return 'aiUsageLog.revise.text.label'
    case GENERATE_IMAGE:
      return 'aiUsageLog.generate.image.label'
    case TEXT_SUMMARIZE:
      return 'aiUsageLog.summarize.text.label'
    case TEXT_RATING:
      return 'aiUsageLog.rating.text.label'
    default:
      return ''
  }
}
